import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Download, Upload, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";

export default function BulkUpload() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  // After commit, populated with {inserted, skipped, errors:[{row, errors|error}]}
  const [commitResult, setCommitResult] = useState(null);

  const onUpload = async () => {
    if (!file) return;
    setBusy(true);
    setPreview(null);
    setCommitResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/employees/bulk-preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to parse file");
    } finally {
      setBusy(false);
    }
  };

  const onCommit = async () => {
    if (!preview) return;
    if (preview.valid === 0) {
      toast.error("No valid rows to import");
      return;
    }
    setBusy(true);
    setCommitResult(null);
    try {
      const { data } = await api.post("/employees/bulk-commit", preview.rows);
      const autoDepts = data.auto_created_departments || [];
      let msg = `Imported ${data.inserted}, skipped ${data.skipped}`;
      if (autoDepts.length) {
        msg += ` · ${autoDepts.length} new department${autoDepts.length === 1 ? "" : "s"} auto-created`;
      }
      if (data.skipped > 0 && data.inserted === 0) {
        // All rows failed — keep user on this page with error details
        toast.error(msg);
        setCommitResult(data);
      } else if (data.skipped > 0) {
        // Partial success — also show details
        toast.warning(msg);
        setCommitResult(data);
      } else {
        toast.success(msg);
        navigate("/employees");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/employees/csv-template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "employee_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download template");
    }
  };

  return (
    <div data-testid="bulk-upload-page" className="space-y-6 max-w-6xl">
      <div className="flex items-center gap-3">
        <Link to="/employees" className="text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Bulk import</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Upload employee CSV
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Download the template, fill it in, then upload to preview validation results.
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" data-testid="download-template-btn" onClick={downloadTemplate}>
            <Download className="h-4 w-4 mr-2" />
            Download CSV template
          </Button>
        </div>

        <div className="border-2 border-dashed border-slate-300 rounded-md p-8 text-center">
          <Upload className="h-8 w-8 text-slate-400 mx-auto mb-3" />
          <input
            data-testid="file-input"
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }}
            className="block mx-auto text-sm"
          />
          {file && (
            <div className="mt-3 text-xs text-slate-600">
              Selected: <span className="font-mono">{file.name}</span> ({Math.round(file.size / 1024)} KB)
            </div>
          )}
          <Button
            data-testid="parse-btn"
            onClick={onUpload}
            disabled={!file || busy}
            className="mt-4 brand-bg brand-bg-hover text-white"
          >
            {busy ? "Parsing..." : "Parse & preview"}
          </Button>
        </div>
      </div>

      {preview && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="p-5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <div className="text-sm">
                <span className="text-slate-500">Total:</span>{" "}
                <span className="font-semibold text-slate-900">{preview.total}</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-green-700">
                <CheckCircle2 className="h-4 w-4" />
                {preview.valid} valid
              </div>
              <div className="flex items-center gap-1.5 text-sm text-red-700">
                <AlertTriangle className="h-4 w-4" />
                {preview.invalid} with errors
              </div>
            </div>
            <Button
              data-testid="commit-btn"
              onClick={onCommit}
              disabled={busy || preview.valid === 0}
              className="brand-bg brand-bg-hover text-white"
            >
              Import {preview.valid} valid row{preview.valid === 1 ? "" : "s"}
            </Button>
          </div>
          <div className="overflow-x-auto max-h-[600px]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b sticky top-0">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2">#</th>
                  <th className="px-4 py-2">Punch ID</th>
                  <th className="px-4 py-2">Code</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2">Unit / Dept</th>
                  <th className="px-4 py-2">CTC</th>
                  <th className="px-4 py-2">Issues</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {preview.rows.map((r) => (
                  <tr
                    key={r.row}
                    className={r.valid ? "" : "bg-red-50"}
                    data-testid={`preview-row-${r.row}`}
                  >
                    <td className="px-4 py-2 text-slate-500">{r.row}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.raw.punch_id}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.raw.employee_code}</td>
                    <td className="px-4 py-2 font-medium">{r.raw.name}</td>
                    <td className="px-4 py-2 text-xs">
                      {r.raw.category ? (
                        <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-medium">
                          {r.raw.category}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600">
                      {r.raw.unit_name} / {r.raw.department_name}
                      {r.department_will_auto_create && (
                        <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] uppercase font-semibold tracking-wider">
                          new
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 tabular-nums">{r.raw.ctc}</td>
                    <td className="px-4 py-2">
                      {r.errors.length === 0 ? (
                        <span className="text-green-700 text-xs">OK</span>
                      ) : (
                        <ul className="text-xs text-red-700 list-disc pl-4">
                          {r.errors.map((er, i) => <li key={i}>{er}</li>)}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {commitResult && (
        <div className="bg-white border-2 border-red-200 rounded-md overflow-hidden" data-testid="commit-result-panel">
          <div className="p-4 bg-red-50 border-b border-red-200 flex items-start gap-3">
            <XCircle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-red-900 text-sm">
                Import result: {commitResult.inserted} inserted, {commitResult.skipped} skipped
              </div>
              <div className="text-xs text-red-700 mt-1">
                {commitResult.inserted === 0
                  ? "No rows were imported. See the row-level errors below — fix them in your file and re-upload."
                  : "Some rows failed. Inspect the errors below; you can fix and re-import only those rows."}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCommitResult(null)}
              data-testid="dismiss-result-btn"
            >
              Dismiss
            </Button>
          </div>
          {commitResult.errors && commitResult.errors.length > 0 && (
            <div className="overflow-x-auto max-h-[400px]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b sticky top-0">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-2">Row #</th>
                    <th className="px-4 py-2">Why it was skipped</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {commitResult.errors.map((er, i) => {
                    // Backend returns either {row, errors: []} or {row, error: str}
                    const msgs = er.errors || (er.error ? [er.error] : ["Unknown error"]);
                    return (
                      <tr key={i} className="bg-red-50/40" data-testid={`commit-error-${er.row || i}`}>
                        <td className="px-4 py-2 text-slate-700 font-mono text-xs align-top">{er.row || "—"}</td>
                        <td className="px-4 py-2">
                          <ul className="text-xs text-red-800 list-disc pl-4 space-y-0.5">
                            {msgs.map((m, j) => <li key={j}>{m}</li>)}
                          </ul>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
